/**
 * ChatDrawerSlot — shared right-side chat drawer mounted at shell level by
 * Dashboard and Commerce (Phase 2). IDE continues to host its own chat
 * inside CanvasArea (left-side) — moving IDE's chat is deliberately deferred
 * to Phase 3 to keep Phase 2 scope tight and to preserve IDE's
 * open-by-default, left-docked UX without user-visible regression.
 *
 * Per IA §5.1 + §5.3 — every shell uses `ResponsivePanel` with `side: 'right'`,
 * `id: 'chat'`. Width is persisted via `chatPanelWidth`. The drawer renders
 * inline at wide desktop breakpoints and as a sheet on compact workspaces so
 * chat never permanently removes the width needed by the active editor.
 *
 * Open/close is gated on `chatSidebarOpen` from the store. Default state per
 * shell is installed via `useChatDefaultForShell` which runs once when the
 * shell mounts — see `shell-chat-defaults.ts` for the rationale.
 */
import { useEffect, useState } from 'react'
import { useStudioStore } from '@/store/studio-store'
import { useUIBreakpoint } from '@/hooks/use-ui-breakpoint'
import { ResponsivePanel } from './responsive-panel'
import { ChatPanel } from './chat-panel'
import { clampChatPanelWidth } from '@/store/ui-slice'
import { SelectionContextPanel } from './ide-v2/selection-context-panel'
import { ConversationTargetSummary } from './ide-v2/components/conversation-target-summary'
import { useStudioLocale } from '@/i18n'
import { COOGO_IDE_CHROME_COPY } from './ide-v2/coogo-ide-chrome-copy'
import { TemplateContentPanel } from '@/features/templates/content-form/TemplateContentPanel'
import { deriveStudioInteractionCapabilities } from './ide-v2/studio-interaction-capabilities'
import { useContextualChatDisclosure } from './use-contextual-chat-disclosure'

export function ChatDrawerSlot() {
  const { locale } = useStudioLocale()
  const copy = COOGO_IDE_CHROME_COPY[locale]
  const chatSidebarOpen = useStudioStore((s) => s.chatSidebarOpen)
  const handleChatOpenChange = useContextualChatDisclosure()
  const chatPanelWidth = useStudioStore((s) =>
    clampChatPanelWidth(s.chatPanelWidth),
  )
  const setChatPanelWidth = useStudioStore((s) => s.setChatPanelWidth)
  // D1 (operator-reported overlap, design-review 2026-07-27) — the chat rail
  // must reserve layout space at every desktop width instead of covering the
  // work surface. `inlineFrom="lg"` keeps the rail an inline flex sibling from
  // 1024px up (the 1024–1279px band previously fell into the Sheet overlay and
  // read as "left page and right chat overlap" while also blocking top-bar
  // navigation behind the backdrop). At `lg` the persisted width is capped at
  // 360px so the primary work column keeps usable width on the narrowest
  // inline band; the user's stored preference is untouched.
  const { bp } = useUIBreakpoint()
  const inlineChatPanelWidth =
    bp === 'lg' ? Math.min(chatPanelWidth, 360) : chatPanelWidth
  // The resize handle must persist the same value the panel renders —
  // otherwise dragging past the lg cap silently stores an invisible wider
  // width (rubber-band feel now, unexplained width jump when the window
  // later grows to xl).
  const handleChatPanelResize = (width: number) =>
    setChatPanelWidth(bp === 'lg' ? Math.min(width, 360) : width)
  const inspectorPanelOpen = useStudioStore((s) => s.inspectorPanelOpen)
  const setInspectorPanelOpen = useStudioStore((s) => s.setInspectorPanelOpen)
  const currentProject = useStudioStore((s) => s.currentProject)
  const currentProjectId = useStudioStore((s) => s.currentProjectId)
  const projectMeta = useStudioStore((s) => s.projectMeta)
  const transaction = useStudioStore((s) => s.agenticUxTransaction)
  const [contentTabOpen, setContentTabOpen] = useState(false)
  const capabilities = deriveStudioInteractionCapabilities({
    currentProjectId,
    currentProject,
    projectMetadata:
      (projectMeta?.metadata as Record<string, unknown> | undefined) ?? null,
  })

  // Template package authoring belongs to its authoring workspace. Keeping it
  // out of the primary Chat tab strip preserves this drawer for conversation,
  // selected context, and instantiated-template content only.
  const activeTab: 'chat' | 'context' | 'content' =
    contentTabOpen && capabilities.hasTemplateContent
      ? 'content'
      : inspectorPanelOpen
        ? 'context'
        : 'chat'

  useEffect(() => {
    if (!transaction || transaction.status !== 'running') return
    if (
      transaction.phase !== 'route-committed' &&
      transaction.phase !== 'project-opened'
    )
      return
    setContentTabOpen(false)
  }, [transaction])

  return (
    <ResponsivePanel
      id="chat"
      side="right"
      inlineFrom="lg"
      modalFrom="md"
      open={chatSidebarOpen}
      onOpenChange={handleChatOpenChange}
      size={inlineChatPanelWidth}
      onResize={handleChatPanelResize}
      sheetClassName="!w-[calc(100vw-3.5rem)] !max-w-[calc(100vw-3.5rem)] !p-0 sm:!w-[28rem] sm:!max-w-[28rem]"
    >
      <div className="h-full border-l border-border flex flex-col overflow-hidden bg-background">
        <div
          data-testid="studio-interaction-tabs"
          className="flex h-10 shrink-0 items-center gap-1 overflow-x-auto border-b border-border bg-card px-2"
        >
          {capabilities.tabs.map((tab) => (
            <button
              key={tab}
              type="button"
              data-testid={tab === 'content' ? 'template-content-tab' : undefined}
              onClick={() => {
                setContentTabOpen(tab === 'content')
                setInspectorPanelOpen(tab === 'context')
              }}
              aria-pressed={activeTab === tab}
              className={`h-7 min-w-0 flex-1 rounded-md px-2 text-xs font-medium transition-colors ${
                activeTab === tab
                  ? 'border border-border/70 bg-muted text-foreground'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              <span className="block truncate">
                {tab === 'chat' ? copy.chat : tab === 'context' ? copy.context : copy.content}
              </span>
            </button>
          ))}
        </div>
        {activeTab === 'content' ? (
          <TemplateContentPanel />
        ) : activeTab === 'context' ? (
          <SelectionContextPanel
            onShowChat={() => {
              setContentTabOpen(false)
              setInspectorPanelOpen(false)
            }}
          />
        ) : (
          <div className="flex-1 min-h-0 flex flex-col">
            <ConversationTargetSummary
              onChangeTarget={() => setInspectorPanelOpen(true)}
            />
            <ChatPanel />
          </div>
        )}
      </div>
    </ResponsivePanel>
  )
}
